import * as React from 'react';
import { UploadOutlined } from '@ant-design/icons';
import { Button, Upload, message, Modal, Input, Skeleton } from 'antd';
import { getBackendUri } from '../../utils/EnvConfigUtils';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { retrieveCachedUserDetails } from '../../thunks/session_thunks';
import { withUserType } from '../withUserType';
import AIIcon from '../AIIcon';
import { owuploadCustomRequest } from '../../thunks/file_thunks';
import { getProtectedImageUriGeneric } from '../../utils/FileAccessUtils';

const { TextArea } = Input;

export function fileUploadValidation(file, fileMimeType) {
	const fileMimeTypes = [fileMimeType];
	const isFileType = fileMimeTypes.indexOf(file.type) !== -1;
	if (!isFileType) {
		message.error(`Uploaded file is not of the expected type: ${fileMimeType}`);
	}
	return isFileType;
}

const loadingCopies = [
	'Reading and structuring invoice data...',
	'Parsing invoice fields and values...',
	'Extracting vendor and billing details...',
	'Organizing charges and fees...',
	'Checking line items, taxes, and totals...',
	'Looking for discounts and adjustments...',
	'Validating amounts and calculations...',
	'Reviewing totals for accuracy...',
	'Preparing structured invoice data for review...',
	'Finalizing extracted data...',
];

export const InvoiceLoadingSkeleton: React.FC<{ currentLoadingCopyIndex?: number }> = ({
	currentLoadingCopyIndex = 0,
}) => {
	return (
		<div style={{ maxWidth: '800px', margin: '0 auto' }}>
			<div className="h3medium mb-6 text-left">{loadingCopies[currentLoadingCopyIndex]}</div>
			{/* Invoice Header - Company & Invoice Info */}
			<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '32px' }}>
				<Skeleton.Input active style={{ width: '180px' }} size="small" />
				<Skeleton.Input active style={{ width: '150px' }} size="small" />
			</div>

			{/* Bill To / Ship To Section */}
			<div style={{ display: 'flex', gap: '40px', marginBottom: '32px' }}>
				<div style={{ flex: 1 }}>
					<Skeleton active paragraph={{ rows: 3 }} title={false} />
				</div>
				<div style={{ flex: 1 }}>
					<Skeleton active paragraph={{ rows: 3 }} title={false} />
				</div>
			</div>

			{/* Line Items Table Header */}
			<div style={{ marginBottom: '16px' }}>
				<Skeleton.Input active style={{ width: '100%', height: '32px' }} />
			</div>

			{/* Line Items Rows */}
			<Skeleton active paragraph={{ rows: 4 }} title={false} style={{ marginBottom: '24px' }} />

			{/* Totals Section */}
			<div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '32px' }}>
				<div style={{ width: '300px' }}>
					<Skeleton active paragraph={{ rows: 3 }} title={false} />
				</div>
			</div>
		</div>
	);
};

export const InvoiceLoadingSkeletonAnimated: React.FC = () => {
	const [currentLoadingCopyIndex, setCurrentLoadingCopyIndex] = React.useState(0);

	React.useEffect(() => {
		const interval = setInterval(() => {
			setCurrentLoadingCopyIndex((prev) => (prev + 1) % loadingCopies.length);
		}, 2500);

		return () => clearInterval(interval);
	}, []);

	return <InvoiceLoadingSkeleton currentLoadingCopyIndex={currentLoadingCopyIndex} />;
};

class InvoiceUploadandParse extends React.Component<any, any> {
	loadingCopyInterval: any = null;

	constructor(props) {
		super(props);
		this.state = {
			uploadLoading: false,
			fileList: [],
			parsingLoading: false,
			fileName: '',
			localFileUrl: null,
			fileObject: null,
			promptModalVisible: false,
			currentLoadingCopyIndex: 0,
			s3FileUrl: null,
			prompt: `Analyze this invoice PDF and extract the data with DISCOUNT DISTRIBUTED across line items.

{
  "invoiceTotalAfterTax": <number>,
  "tax": <number>,
  "discount": <number>,
  "discount_percentage": <number>,
  "invoiceTotalBeforeTax": <number>,
  "line_items": [
    {
      "description": "detailed description of the service or product",
      "quantity": <number>,
      "unit_price": <number>,
      "extended_price": <number>,
      "extended_price_after_discount": <number>,
      "discount_amount": <number>,
      "category": "labor|part|equipment|travel|freight|other"
    }
  ],
  "scope": "overall scope of work or project description if available",
  "invoice_number": "invoice number if present",
  "invoice_date": "invoice date if present",
  "vendor_name": "vendor/company name",
  "customer_name": "customer name if present"
}

IMPORTANT INSTRUCTIONS:
1. Extract ALL individual line items from the invoice - DO NOT include subtotal rows (like "Labor Total", "Parts Total", etc.)
2. For each line item:
   - Calculate extended_price = quantity × unit_price (ORIGINAL price, before discount)
   - For time-based labor (e.g., "120 minutes" or "2 hours"), convert to hours for quantity:
     * If item shows minutes: quantity = minutes / 60 (e.g., 120 minutes = 2.0 hours)
     * If item shows hours: use the hour value as quantity
   - Use the actual line item description, not summary/subtotal labels
3. Categorize each line item as: labor, part, equipment, travel, freight, or other
   - labor: labor/work performed (hourly charges, service time, installation labor)
   - part: small components, consumables, supplies (bulbs, screws, wire, filters, etc.)
   - equipment: larger fixtures, installed equipment (HVAC units, light fixtures, motors, etc.)
   - travel: travel costs, tolls, parking, mileage, trip charges
   - freight: shipping, handling, delivery fees
   - other: anything that doesn't fit above categories
4. invoiceTotalBeforeTax = sum of all line item extended_prices (subtotal BEFORE discount)
5. Extract discount amount from the invoice (e.g., "Discount", "Less Discount", etc.)
   - discount must be returned as a positive number representing money subtracted from the subtotal (even if shown as negative or in parentheses)
6. discount_percentage = discount / invoiceTotalBeforeTax (keep full precision; DO NOT round discount_percentage)
7. Compute each line item's discount_amount and extended_price_after_discount WITH RECONCILIATION:
   - Round all currency values (extended_price, discount_amount, extended_price_after_discount, totals) to 2 decimals using standard half-up rounding.
   - First pass:
     * raw_discount_i = extended_price_i × discount_percentage (full precision)
     * discount_amount_i = round(raw_discount_i, 2)
   - Reconciliation pass (to remove rounding drift):
     * residual = round(discount - sum(discount_amount_i), 2)
     * If residual ≠ 0, distribute residual in $0.01 increments by adjusting discount_amount_i on line items
       in this deterministic order:
         1) largest fractional remainder of raw_discount_i (descending)
         2) larger extended_price_i (descending)
         3) original line order in the invoice
       Continue until residual == 0.00.
   - Then set:
     * extended_price_after_discount_i = round(extended_price_i - discount_amount_i, 2)
8. tax = total tax amount charged
9. invoiceTotalAfterTax = round(invoiceTotalBeforeTax - discount + tax, 2) (final total)
10. Extract the scope of work if described in the invoice
11. All numeric values should be numbers (not strings)
12. If a field is not found, use null for strings or 0 for numbers


CRITICAL RULES:
- Skip any "subtotal" or "category total" rows (e.g., "Labor Total: $382")
- Skip any "discount" rows - extract discount and distribute it using the RECONCILIATION method above
- Only extract actual line items with specific descriptions
- For labor with time durations, always convert to hours in the quantity field
- IMPORTANT: extended_price_after_discount must be calculated for ALL line items, even if discount is 0
- Return ONLY valid JSON. No markdown, no comments, no extra text or words.
- The response must be parseable by json.loads() in Python.


ROUNDING EDGE CASE EXAMPLE:

If invoiceTotalBeforeTax = $100.00 and discount = $10.00:
- Line items: $33.33, $33.33, $33.34

First pass rounded discounts:
- 3.33, 3.33, 3.33 → sum = $9.99

Residual = $0.01
→ Add $0.01 to the line with the largest fractional remainder ($33.34)

Final discounts:
- 3.33, 3.33, 3.34 → sum = $10.00`,
		};
	}

	handleUploadSuccess = (info) => {
		const { handleUploadSuccess } = this.props;
		const fileName = info.file && info.file.name ? info.file.name : '';

		let localFileUrl = this.state.localFileUrl;
		let fileObj = this.state.fileObject;
		const s3FileUrlFromState = this.state.s3FileUrl;

		if (!localFileUrl || !fileObj) {
			if (info.file) {
				if (info.file.originFileObj) {
					fileObj = info.file.originFileObj;
					// Create data URL if we don't have one
					if (!localFileUrl) {
						const reader = new FileReader();
						reader.onload = (e) => {
							const dataUrl = e.target.result;
							this.setState({ localFileUrl: dataUrl });
							// Send updated data to parent
							handleUploadSuccess({
								parsedData: nullSafeGet('data', info),
								fileUrl: s3FileUrlFromState || dataUrl,
								fileName: fileName,
								fileObject: fileObj,
							});
						};
						reader.readAsDataURL(fileObj);
						return;
					}
				} else if (info.file instanceof File || info.file instanceof Blob) {
					fileObj = info.file;
					// Create data URL if we don't have one
					if (!localFileUrl) {
						const reader = new FileReader();
						reader.onload = (e) => {
							const dataUrl = e.target.result;
							this.setState({ localFileUrl: dataUrl });
							// Send updated data to parent
							handleUploadSuccess({
								parsedData: nullSafeGet('data', info),
								fileUrl: s3FileUrlFromState || dataUrl,
								fileName: fileName,
								fileObject: fileObj,
							});
						};
						reader.readAsDataURL(fileObj);
						return;
					}
				}
			}
		}

		this.setState({
			uploadLoading: false,
			parsingLoading: false,
			fileName: fileName,
			localFileUrl: localFileUrl,
			fileObject: fileObj,
		});

		this.clearLoadingCopyInterval();

		if (this.props.onParsingComplete) {
			this.props.onParsingComplete();
		}

		const finalFileUrl = s3FileUrlFromState || localFileUrl;

		handleUploadSuccess({
			parsedData: nullSafeGet('data', info),
			fileUrl: finalFileUrl,
			fileName: fileName,
			fileObject: fileObj,
		});
	};

	onFileChange = ({ file, fileList }) => {
		const isParsingLoading = file.status === 'uploading' || file.status === 'done';

		// Store file details when file is selected
		if (file.status === 'uploading' && file.originFileObj) {
			// Create a data URL instead of blob URL for cross-tab access
			const reader = new FileReader();
			reader.onload = (e) => {
				const dataUrl = e.target.result;
				this.setState({
					uploadLoading: file.status === 'uploading',
					parsingLoading: isParsingLoading,
					localFileUrl: dataUrl,
					fileObject: file.originFileObj,
					fileName: file.name,
				});

				// Notify parent that file is selected and parsing has started
				if (this.props.onFileSelected) {
					this.props.onFileSelected({
						fileUrl: dataUrl,
						fileName: file.name,
						fileObject: file.originFileObj,
					});
				}
			};
			reader.readAsDataURL(file.originFileObj);
		} else {
			this.setState({
				uploadLoading: file.status === 'uploading',
				parsingLoading: isParsingLoading,
			});
		}

		if (isParsingLoading && !this.loadingCopyInterval) {
			this.startLoadingCopyInterval();
			// Notify parent that parsing has started
			if (this.props.onParsingStart) {
				this.props.onParsingStart();
			}
		} else if (!isParsingLoading && this.loadingCopyInterval) {
			this.clearLoadingCopyInterval();
		}
	};

	startLoadingCopyInterval = () => {
		this.setState({ currentLoadingCopyIndex: 0 });

		this.loadingCopyInterval = setInterval(() => {
			this.setState((prevState) => ({
				currentLoadingCopyIndex: (prevState.currentLoadingCopyIndex + 1) % loadingCopies.length,
			}));
		}, 2500);
	};

	clearLoadingCopyInterval = () => {
		if (this.loadingCopyInterval) {
			clearInterval(this.loadingCopyInterval);
			this.loadingCopyInterval = null;
		}
	};

	showPromptModal = () => {
		this.setState({ promptModalVisible: true });
	};

	handlePromptModalOk = () => {
		this.setState({ promptModalVisible: false });
	};

	handlePromptModalCancel = () => {
		this.setState({ promptModalVisible: false });
	};

	handlePromptChange = (e) => {
		this.setState({ prompt: e.target.value });
	};

	componentWillUnmount() {
		this.clearLoadingCopyInterval();

		// Cleanup object URL to prevent memory leaks (if using blob URLs)
		// Note: Data URLs don't need cleanup like blob URLs do
		if (this.state.localFileUrl && this.state.localFileUrl.startsWith('blob:')) {
			URL.revokeObjectURL(this.state.localFileUrl);
		}
	}

	render() {
		const { userType } = this.props;
		const {
			fileList,
			parsingLoading,
			fileName,
			promptModalVisible,
			prompt,
			currentLoadingCopyIndex,
		} = this.state;

		function beforePDFUpload(file) {
			const pdfMimeTypes = ['application/pdf'];
			const isPDF = pdfMimeTypes.indexOf(file.type) !== -1;
			if (!isPDF) {
				message.error('You can only upload PDF files!');
			}
			const isLt2M = file.size / 1024 / 1024 < 4;
			if (!isLt2M) {
				message.error('PDF file size must smaller than 4MB!');
			}
			return isPDF && isLt2M;
		}

		const BACKEND_URI = getBackendUri();
		const token = retrieveCachedUserDetails(['token']).token;
		const action = `${BACKEND_URI}/api/v1/${userType}/invoice/file/invoice_pdf/extract_data`;

		const uploadProps = {
			action,
			onSuccess: this.handleUploadSuccess,
			onError: (error) => {
				console.log('Upload error:', error);
				this.setState({
					uploadLoading: false,
					parsingLoading: false,
				});
				this.clearLoadingCopyInterval();
				// Notify parent that parsing failed
				if (this.props.onParsingComplete) {
					this.props.onParsingComplete();
				}
			},
			fileList: fileList,
			multiple: false,
			beforeUpload: beforePDFUpload,
			showUploadList: { showPreviewIcon: true, showRemoveIcon: true },
			onChange: this.onFileChange,
			headers: { 'X-Auth-Token': token },
			accept: '.pdf',
		};

		// Create custom upload props to include prompt in the request
		const customUploadProps = {
			...uploadProps,
			customRequest: (options) => {
				const { file, onSuccess, onError, onProgress } = options;
				const formData = new FormData();
				formData.append('file', file);
				formData.append('prompt', prompt);

				owuploadCustomRequest({
					userType,
					file,
					onSuccess: ({ data }) => {
						const s3Url = getProtectedImageUriGeneric(data.fileId, data.fileName);
						if (s3Url) {
							this.setState({ s3FileUrl: s3Url });
						}
					},
					onError: (error) => {
						console.error('S3 upload failed for invoice PDF:', error);
					},
				});

				const xhr = new XMLHttpRequest();
				xhr.open('POST', action, true);
				xhr.setRequestHeader('X-Auth-Token', token);

				xhr.upload.onprogress = (e) => {
					if (e.lengthComputable && onProgress) {
						onProgress({ percent: (e.loaded / e.total) * 100 });
					}
				};

				xhr.onload = () => {
					if (xhr.status === 200) {
						try {
							const response = JSON.parse(xhr.responseText);
							if (onSuccess) {
								onSuccess(response, xhr);
							}
						} catch (e) {
							if (onError) {
								onError(e);
							}
						}
					} else {
						if (onError) {
							onError(new Error(`Upload failed with status ${xhr.status}`));
						}
					}
				};

				xhr.onerror = () => {
					if (onError) {
						onError(new Error('Upload failed'));
					}
				};

				xhr.send(formData);
			},
		};

		return (
			<div>
				<Upload.Dragger {...customUploadProps} disabled={parsingLoading}>
					{parsingLoading ? (
						<InvoiceLoadingSkeleton currentLoadingCopyIndex={currentLoadingCopyIndex} />
					) : (
						<div className="flex flex-col items-center justify-center gap-6">
							<div className="rounded border-white  px-4 py-4 text-center ">
								<div className="h4medium mb-2 text-character-primary">
									<AIIcon /> AI Invoice Upload & Classification
								</div>
								<div className="bodyregular text-character-secondary">
									Upload invoices and OpenWrench automatically extracts and classifies each line
									item into labor, materials, parts, tax, and other categories - no manual entry
									required.
								</div>
								<Button className="mt-4" icon={<UploadOutlined />}>
									Click to Upload
								</Button>
							</div>
						</div>
					)}
				</Upload.Dragger>

				<Modal
					title="Set Extraction Prompt"
					visible={promptModalVisible}
					onOk={this.handlePromptModalOk}
					onCancel={this.handlePromptModalCancel}
					okText="Save"
					cancelText="Cancel"
					width={'70vw'}
					bodyStyle={{ height: '70vh' }}
				>
					<div style={{ marginBottom: 16 }}>
						<p style={{ marginBottom: 8, color: '#666' }}>
							Provide a custom prompt to guide the AI extraction process. This will help improve the
							accuracy of data extraction from your invoice.
						</p>
					</div>
					<TextArea
						autoSize={{ minRows: 6, maxRows: 20 }}
						placeholder="Enter your custom prompt here..."
						value={prompt}
						onChange={this.handlePromptChange}
						style={{ marginBottom: 16 }}
					/>
					<div style={{ fontSize: '12px', color: '#999' }}>
						Leave empty to use the default prompt.
					</div>
				</Modal>
			</div>
		);
	}
}

export default withUserType(InvoiceUploadandParse);
